import React, { useReducer } from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  StatusBar,
  Platform,
} from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import { VFText, VFButton } from '..';
import FoundationTestKeys from '../../FoundationTestKeys';
import SpinnerOverlay from '../SpinnerOverlay';
import { useTheme, LightThemeColors } from '../Themes';
import { LottieTypes } from '../VFLottieView';

import { initState, layoutAction, reducer } from './OverlayScreen.reducer';
import styles from './styles';

function OverlayContent({
  centerImage,
  imageStyle,
  closeIcon,
  headerContainerStyle,
  withHeaderTitle,
  headerTitle,
  headerTitleStyle,
  headerKey,
  headerKeyStyle,
  descriptionKey,
  onProceed,
  onCancel,
  onClose,
  hasSingleButton = false,
  okButtonText,
  cancelButtonText,
  descriptionComponent = undefined,
  closeIconAccessabilityLabel,
  iconAccessabilityLabel,
  titleAccessabilityLabel,
  descriptionAccessabilityLabel,
  primaryBtnAccessabilityLabel,
  secondaryBtnAccessabilityLabel,
  allowFontScaling,
  renderContent,
  renderCustomImage,
  vfTextDescriptionProps,
  containerStyle,
  isLoading,
}) {
  const [contentHeight, dispatch] = useReducer(reducer, initState);
  const { height } = Dimensions.get('window');
  const { totalHeight } = contentHeight;
  const isScrollable = totalHeight > height;
  const theme = useTheme() || LightThemeColors;

  function onLayout(event, action) {
    const { height: _height } = event.nativeEvent.layout;
    dispatch({ type: action, height: _height });
  }

  const viewHeightStyle = isScrollable
    ? { minHeight: height }
    : {
        height: Platform.OS === 'android' ? height - StatusBar.currentHeight : height,
      };

  const headerContainerViewStyle = withHeaderTitle ? styles.headerContainer : styles.closeIcon;
  return (
    <ScrollView
      style={[styles.containerScroll(theme), containerStyle]}
      alwaysBounceVertical={isScrollable}
      testID={FoundationTestKeys.overlayContentScrollView}
    >
      {isLoading ? (
        <SpinnerOverlay
          isVisible={isLoading}
          autoPlay
          loop
          lottieStyle={styles.overlayLottie}
          type={LottieTypes.whiteSpinner}
          testID={FoundationTestKeys.overlayContentSpinnerOverlay}
        />
      ) : null}
      <View
        style={[viewHeightStyle, styles.container]}
        testID={FoundationTestKeys.overlayContentMainView}
      >
        <View
          style={[headerContainerViewStyle, headerContainerStyle]}
          onLayout={event => onLayout(event, layoutAction.ON_HEDER_LAYOUT)}
          testID={FoundationTestKeys.overlayContentView}
        >
          {withHeaderTitle && (
            <VFText
              i18nKey={headerTitle}
              style={[styles.headerTitle, headerTitleStyle]}
              allowFontScaling={allowFontScaling}
              testKey={FoundationTestKeys.overlayContentHeaderTitle}
            />
          )}
          <TouchableOpacity onPress={onClose} testID={FoundationTestKeys.overlayContentTO}>
            <Image
              source={closeIcon}
              accessibilityLabel={closeIconAccessabilityLabel}
              testID={closeIconAccessabilityLabel ?? FoundationTestKeys.overlayContentTOImage}
            />
          </TouchableOpacity>
        </View>
        <View
          style={[styles.bodyWrapper, styles.scrollMarginStyle(isScrollable)]}
          onLayout={event => onLayout(event, layoutAction.ON_BODY_LAYOUT)}
          testID={FoundationTestKeys.overlayContentImageView}
        >
          {_isFunction(renderCustomImage) ? (
            renderCustomImage()
          ) : (
            <Image
              source={centerImage}
              style={[styles.image, imageStyle]}
              accessibilityLabel={iconAccessabilityLabel}
              testID={iconAccessabilityLabel ?? FoundationTestKeys.overlayContentImage}
            />
          )}
          <VFText
            i18nKey={headerKey}
            style={[styles.header, headerKeyStyle]}
            testKey={titleAccessabilityLabel ?? FoundationTestKeys.overlayContentTitle}
            allowFontScaling={allowFontScaling}
          />
          {descriptionComponent && _isFunction(descriptionComponent) ? (
            descriptionComponent()
          ) : (
            <VFText
              i18nKey={descriptionKey}
              style={styles.description}
              testKey={
                descriptionAccessabilityLabel ?? FoundationTestKeys.overlayContentDescription
              }
              allowFontScaling={allowFontScaling}
              {...(vfTextDescriptionProps || {})}
            />
          )}
          {_isFunction(renderContent) ? renderContent() : null}
        </View>

        <View
          style={styles.buttonsWrappers}
          onLayout={event => onLayout(event, layoutAction.ON_CTA_LAYOUT)}
          testID={FoundationTestKeys.overlayContentButtonsView}
        >
          <VFButton
            onPress={onProceed}
            title={okButtonText}
            style={styles.proceed}
            testKey={primaryBtnAccessabilityLabel ?? FoundationTestKeys.overlayContentButton}
            allowFontScaling={allowFontScaling}
          />
          {!hasSingleButton && (
            <VFButton
              onPress={onCancel}
              title={cancelButtonText}
              type="secondary"
              testKey={
                secondaryBtnAccessabilityLabel ?? FoundationTestKeys.overlayContentSecondaryButton
              }
              allowFontScaling={allowFontScaling}
            />
          )}
        </View>
      </View>
    </ScrollView>
  );
}
OverlayContent.defaultProps = {
  withHeaderTitle: false,
  isLoading: false,
};
OverlayContent.propTypes = {
  centerImage: PropTypes.shape({ uri: PropTypes.string.isRequired }),
  imageStyle: ViewPropTypes.style,
  closeIcon: PropTypes.shape({ uri: PropTypes.string.isRequired }),
  headerKey: PropTypes.string,
  headerKeyStyle: TextPropTypes.style,
  descriptionKey: PropTypes.string,
  onProceed: PropTypes.func,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  hasSingleButton: PropTypes.bool,
  okButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  descriptionComponent: PropTypes.func,
  closeIconAccessabilityLabel: PropTypes.string,
  iconAccessabilityLabel: PropTypes.string,
  titleAccessabilityLabel: PropTypes.string,
  descriptionAccessabilityLabel: PropTypes.string,
  primaryBtnAccessabilityLabel: PropTypes.string,
  secondaryBtnAccessabilityLabel: PropTypes.string,
  allowFontScaling: PropTypes.bool,
  renderCustomImage: PropTypes.func,
  renderContent: PropTypes.func,
  containerStyle: ViewPropTypes.style,
  vfTextDescriptionProps: PropTypes.object,
  headerContainerStyle: ViewPropTypes.style,
  withHeaderTitle: PropTypes.bool,
  headerTitle: PropTypes.string,
  headerTitleStyle: ViewPropTypes.style,
  isLoading: PropTypes.bool,
};

export default OverlayContent;
